import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { leadsApi } from "../api/leads.js";
import { ordersApi } from "../api/orders.js";
import { productsApi } from "../api/products.js";
import { customizationsApi } from "../api/customizations.js";
import { usersApi } from "../api/users.js";
import { crmApiInstance } from "../api/client.js";
import {
  Search, X, ChevronRight, ChevronLeft, Check,
  User, Tag, Package, MapPin, FileCheck,
  Plus, Trash2, IndianRupee, CalendarDays,
  Loader2, ShoppingBag, ClipboardList,
  AlertCircle, Settings, Upload, FileText,
  Building2, GitBranch, Pencil, Info, Lock,
} from "lucide-react";
import "./PlaceOrder.css";

/* ─── Steps ──────────────────────────────────────────────────────── */
const STEPS_ORG = [
  { label: "Customer", icon: User },
  { label: "Products", icon: Package },
  { label: "Branch Qty", icon: GitBranch },
  { label: "Review", icon: FileCheck },
];

const STEPS_REGULAR = [
  { label: "Customer", icon: User },
  { label: "Products", icon: Package },
  { label: "Location", icon: MapPin },
  { label: "Review", icon: FileCheck },
];

/* ─── Empty cart row ─────────────────────────────────────────────── */
const emptyRow = () => ({
  _id: crypto.randomUUID(),
  productId: null,
  name: "",
  sku: "",
  qty: 1,
  price: "",
  minPrice: 0,
});

/* ─── Tiny debounce hook ──────────────────────────────────────────── */
function useDebounce(val, ms = 350) {
  const [deb, setDeb] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setDeb(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return deb;
}

const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

const formatAddress = (loc) => {
  if (!loc) return "";
  return [loc.address_line_1, loc.address_line_2, loc.city, loc.state, loc.pincode, loc.country]
    .filter(Boolean).join(", ");
};

/* ═══════════════════════════════════════════════════════════════════
   STEPPER HEADER
════════════════════════════════════════════════════════════════════ */
const Stepper = ({ step, steps }) => (
  <div className="po-stepper">
    {steps.map(({ label, icon: Icon }, i) => {
      const done = i < step;
      const active = i === step;
      return (
        <div key={label} className="po-step-wrap">
          <div className={`po-step ${done ? "po-step--done" : active ? "po-step--active" : ""}`}>
            <div className="po-step-circle">
              {done ? <Check size={14} strokeWidth={3} /> : <Icon size={14} strokeWidth={2} />}
            </div>
            <span className="po-step-label">{label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`po-step-line ${done ? "po-step-line--done" : ""}`} />
          )}
        </div>
      );
    })}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   STEP 0 — SELECT CUSTOMER (or locked ORG card)
════════════════════════════════════════════════════════════════════ */
const StepCustomer = ({ selected, onSelect, lastOrder, loadingLastOrder, onReorder, orgMode, orgName, clientType, onChangeClientType, productType, onChangeProductType, salesExec, onChangeSalesExec, allSalesStaff, onNext, canNext }) => {
  const [query, setQuery] = useState(selected ? selected.name : "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debQ = useDebounce(query, 350);
  const [execQuery, setExecQuery] = useState(salesExec || "");
  const [execOpen, setExecOpen] = useState(false);
  const execFiltered = (allSalesStaff || []).filter(u =>
    !execQuery || (u.name || "").toLowerCase().includes(execQuery.toLowerCase()) || (u.email || "").toLowerCase().includes(execQuery.toLowerCase())
  );

  useEffect(() => {
    if (orgMode) return;
    if (!debQ || (selected && debQ === selected.name)) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    leadsApi.getAll({ search: debQ, limit: 20 })
      .then(res => {
        if (cancelled) return;
        const data = res.data?.data || res.data;
        const arr = data?.data || data?.rows || data || [];
        setResults(Array.isArray(arr) ? arr.map(c => ({
          id: c.id, name: c.name, phone: c.phone || "",
          email: c.email || "", gst_number: c.gst_number || "",
        })) : []);
      })
      .catch(() => setResults([]))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debQ, selected, orgMode]);

  const pick = (c) => { onSelect(c); setQuery(c.name); setResults([]); };
  const clearSel = () => { setQuery(""); onSelect(null); setResults([]); };

  // ── Org mode (locked) ──
  if (orgMode) {
    return (
      <div className="po-step-body">
        <div className="po-step-title"><Building2 size={18} /> Organization Order</div>
        <p className="po-step-desc">You are placing a bulk order for the entire organization. Quantities per branch will be configured in the Branch Quantities step.</p>
        <div className="po-cust-selected" style={{ border: "2px solid var(--primary)", background: "var(--primary-glow)" }}>
          <div className="po-cust-sel-left">
            <div className="po-cust-avatar po-cust-avatar--lg" style={{ background: "var(--primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><Building2 size={16} /></div>
            <div>
              <div className="po-cust-name">{orgName}</div>
              <div className="po-cust-meta"><span style={{ color: "var(--primary)", fontWeight: 600 }}>Organization · Bulk Order</span></div>
            </div>
          </div>
          <span className="po-cust-check" style={{ background: "rgba(0,140,61,0.12)", color: "var(--primary)" }}><Check size={14} /> Organization Selected</span>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", marginBottom: 6, color: "var(--text)" }}>Customer Type <span style={{ color: "var(--danger, #ef4444)" }}>*</span></label>
          <select value={clientType} onChange={e => onChangeClientType(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--radius-sm, 6px)", border: "1.5px solid " + (clientType ? "var(--primary)" : "var(--border-2)"), background: "#fff", fontSize: "0.88rem", fontWeight: 500, cursor: "pointer", outline: "none", appearance: "auto" }}>
            <option value="">— Select Customer Type —</option>
            <option value="Place Order">Place Order</option>
            <option value="New Client Onboarding">New Client Onboarding</option>
          </select>
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", marginBottom: 6, color: "var(--text)" }}>Product Category <span style={{ color: "var(--danger, #ef4444)" }}>*</span></label>
          <select value={productType} onChange={e => onChangeProductType(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--radius-sm, 6px)", border: "1.5px solid " + (productType ? "var(--primary)" : "var(--border-2)"), background: "#fff", fontSize: "0.88rem", fontWeight: 500, cursor: "pointer", outline: "none", appearance: "auto" }}>
            <option value="">— Select Product Category —</option>
            <option value="local_retail">Local Retail</option>
            <option value="printed">Printed</option>
            <option value="retail_big">Retail Big</option>
            <option value="inter_state_retail">Inter-State Retail</option>
          </select>
        </div>

        <div style={{ marginTop: 14, marginBottom: 4, position: "relative" }}>
          <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", marginBottom: 6, color: "var(--text)" }}>Sales Executive <span style={{ color: "var(--danger, #ef4444)" }}>*</span></label>
          <input type="text" value={execQuery} placeholder="Search sales executive…" autoComplete="off"
            onChange={e => { setExecQuery(e.target.value); setExecOpen(true); if (!e.target.value) onChangeSalesExec(""); }}
            onFocus={() => setExecOpen(true)} onBlur={() => setTimeout(() => setExecOpen(false), 160)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--radius-sm, 6px)", border: "1.5px solid " + (salesExec ? "var(--primary)" : "var(--border-2)"), fontSize: "0.88rem", fontWeight: 500, outline: "none", boxSizing: "border-box" }} />
          {execOpen && execFiltered.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1.5px solid var(--border-2)", borderRadius: "var(--radius-sm, 6px)", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 200, maxHeight: 200, overflowY: "auto" }}>
              {execFiltered.map(u => (
                <div key={u.id} onMouseDown={() => { onChangeSalesExec(u.name || u.email); setExecQuery(u.name || u.email); setExecOpen(false); }}
                  style={{ padding: "9px 12px", cursor: "pointer", borderBottom: "1px solid var(--border)" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--bg-2,#f4f6f8)"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                  <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{u.name}</div>
                  {u.email && <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{u.email}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button className="btn-primary po-nav-btn" onClick={onNext} disabled={!canNext}>
            Next: Products <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  const hasSelection = !!selected;

  return (
    <div className={`po-cust-split-wrap${hasSelection ? " po-cust-split-wrap--active" : ""}`}>
      {/* ── Left panel: Search ── */}
      <div className="po-cust-split-left">
        <div className="po-step-title"><User size={18} /> Search &amp; Select Customer</div>
        <div className="po-cust-search-wrap">
          <Search size={14} className="po-cust-search-ico" />
          <input className="po-cust-search-input" value={query}
            onChange={e => { setQuery(e.target.value); if (selected) onSelect(null); }}
            placeholder="Type name, phone or email…" autoFocus />
          {query && <button className="po-cust-search-clear" onClick={clearSel}><X size={12} /></button>}
          {loading && <Loader2 size={14} className="spin po-cust-search-ico" />}
        </div>

        {results.length > 0 && (
          <div className="po-cust-results">
            {results.map(c => (
              <div key={c.id} className="po-cust-item" onClick={() => pick(c)}>
                <div className="po-cust-avatar">{c.name[0]?.toUpperCase()}</div>
                <div className="po-cust-info">
                  <div className="po-cust-name">{c.name}</div>
                  <div className="po-cust-meta">
                    {c.phone && <span>{c.phone}</span>}
                    {c.gst_number && <span className="mono">GST: {c.gst_number}</span>}
                    {c.email && <span>{c.email}</span>}
                  </div>
                </div>
                <div className="po-cust-id">#{c.id}</div>
              </div>
            ))}
          </div>
        )}

        {selected && (
          <div className="po-cust-selected">
            <div className="po-cust-sel-left">
              <div className="po-cust-avatar po-cust-avatar--lg">{selected.name[0]?.toUpperCase()}</div>
              <div>
                <div className="po-cust-name">{selected.name}</div>
                <div className="po-cust-meta">
                  {selected.phone && <span>{selected.phone}</span>}
                  {selected.gst_number && <span className="mono">GST: {selected.gst_number}</span>}
                  {selected.email && <span>{selected.email}</span>}
                </div>
              </div>
            </div>
            <span className="po-cust-check"><Check size={14} /> Selected</span>
          </div>
        )}

        {selected && (
          <div style={{ marginTop: 4 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--text)" }}>Order History</span>
            </div>
            {loadingLastOrder ? (
              <div style={{ textAlign: "center", padding: "16px 0", color: "var(--text-muted)", fontSize: "0.82rem" }}><Loader2 size={14} className="spin" style={{ marginRight: 4 }} />Loading...</div>
            ) : lastOrder ? (
              <div className="po-reorder-banner">
                <div className="po-reorder-info">
                  <ClipboardList size={16} className="po-reorder-icon" />
                  <div>
                    <div className="po-reorder-title">Previous Order Found</div>
                    <div className="po-reorder-desc">Last ordered on <strong>{formatDate(lastOrder.created_at)}</strong> (₹{parseFloat(lastOrder.total_amount).toLocaleString("en-IN")})</div>
                  </div>
                </div>
                <button className="po-reorder-btn" type="button" onClick={onReorder}>Place Previous Order</button>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "16px 0", color: "var(--text-muted)", fontSize: "0.82rem" }}>No orders yet.</div>
            )}
          </div>
        )}
      </div>

      {/* ── Right panel: shown when customer selected ── */}
      {hasSelection && (
        <div className="po-cust-split-right">
          <div className="po-cust-right-header">
            <div className="po-cust-right-check"><Check size={14} strokeWidth={3} /></div>
            Customer Selected
          </div>

          <div className="po-cust-right-info-card">
            <div className="po-cust-avatar po-cust-avatar--lg">{selected.name[0]?.toUpperCase()}</div>
            <div>
              <div className="po-cust-name">{selected.name}</div>
              <div className="po-cust-meta">
                {selected.phone && <span>{selected.phone}</span>}
                {selected.gst_number && <span>GST: {selected.gst_number}</span>}
                {selected.email && <span>{selected.email}</span>}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 4 }}>
            <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", marginBottom: 6, color: "var(--text)" }}>Customer Type <span style={{ color: "var(--danger, #ef4444)" }}>*</span></label>
            <select value={clientType} onChange={e => onChangeClientType(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--radius-sm, 6px)", border: "1.5px solid " + (clientType ? "var(--primary)" : "var(--border-2)"), background: "#fff", fontSize: "0.88rem", fontWeight: 500, cursor: "pointer", outline: "none", appearance: "auto" }}>
              <option value="">— Select Customer Type —</option>
              <option value="new">New</option>
              <option value="old">Old</option>
            </select>
          </div>

          <div>
            <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", marginBottom: 6, color: "var(--text)" }}>Product Category <span style={{ color: "var(--danger, #ef4444)" }}>*</span></label>
            <select value={productType} onChange={e => onChangeProductType(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--radius-sm, 6px)", border: "1.5px solid " + (productType ? "var(--primary)" : "var(--border-2)"), background: "#fff", fontSize: "0.88rem", fontWeight: 500, cursor: "pointer", outline: "none", appearance: "auto" }}>
              <option value="">— Select Product Category —</option>
              <option value="local_retail">Local Retail</option>
              <option value="printed">Printed</option>
              <option value="retail_big">Retail Big</option>
              <option value="inter_state_retail">Inter-State Retail</option>
            </select>
          </div>

          <div style={{ position: "relative" }}>
            <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", marginBottom: 6, color: "var(--text)" }}>Sales Executive <span style={{ color: "var(--danger, #ef4444)" }}>*</span></label>
            <input type="text" value={execQuery} placeholder="Search sales executive…" autoComplete="off"
              onChange={e => { setExecQuery(e.target.value); setExecOpen(true); if (!e.target.value) onChangeSalesExec(""); }}
              onFocus={() => setExecOpen(true)} onBlur={() => setTimeout(() => setExecOpen(false), 160)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--radius-sm, 6px)", border: "1.5px solid " + (salesExec ? "var(--primary)" : "var(--border-2)"), fontSize: "0.88rem", fontWeight: 500, outline: "none", boxSizing: "border-box" }} />
            {execOpen && execFiltered.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1.5px solid var(--border-2)", borderRadius: "var(--radius-sm, 6px)", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 200, maxHeight: 200, overflowY: "auto" }}>
                {execFiltered.map(u => (
                  <div key={u.id} onMouseDown={() => { onChangeSalesExec(u.name || u.email); setExecQuery(u.name || u.email); setExecOpen(false); }}
                    style={{ padding: "9px 12px", cursor: "pointer", borderBottom: "1px solid var(--border)" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--bg-2,#f4f6f8)"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{u.name}</div>
                    {u.email && <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{u.email}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   STEP 1 — PRODUCT TYPE
════════════════════════════════════════════════════════════════════ */
const StepProductType = ({ value, onChange }) => {
  const types = [
    { key: "local_retail", icon: Package, title: "Local Retail", desc: "Standard local retail orders for nearby customers." },
    { key: "printed", icon: Tag, title: "Printed", desc: "Products with custom print, branding, or logo specifications." },
    { key: "retail_big", icon: ShoppingBag, title: "Retail Big", desc: "Bulk or large-volume retail orders." },
    { key: "inter_state_retail", icon: MapPin, title: "Inter-State Retail", desc: "Orders dispatched across state lines." },
  ];

  return (
    <div className="po-step-body">
      <div className="po-step-title"><Tag size={18} /> Choose Product Type</div>
      <p className="po-step-desc">Select whether you want to order plain products or customized products with custom print specifications.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px", marginTop: "12px" }}>
        {types.map(({ key, icon: Icon, title, desc }) => (
          <div
            key={key}
            onClick={() => onChange(key)}
            style={{
              border: `2.5px solid ${value === key ? "var(--primary)" : "var(--border-2, #e2e8f0)"}`,
              borderRadius: "12px", padding: "24px", cursor: "pointer",
              background: value === key ? "rgba(99,102,241,0.03)" : "#fff",
              transition: "all 0.2s", display: "flex", flexDirection: "column",
              alignItems: "center", gap: "12px", textAlign: "center",
              boxShadow: value === key ? "0 4px 16px rgba(0,0,0,0.1)" : "0 1px 4px rgba(0,0,0,0.06)",
              transform: value === key ? "scale(1.02)" : "scale(1)",
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: value === key ? "var(--primary)" : "#f1f5f9",
              color: value === key ? "#fff" : "#94a3b8",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: value === key ? "0 4px 12px rgba(99,102,241,0.3)" : "none",
            }}>
              <Icon size={24} />
            </div>
            <div>
              <h3 style={{ fontSize: "1.05rem", fontWeight: 700, margin: "0 0 6px", color: "var(--text)" }}>{title}</h3>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: 0, lineHeight: 1.45 }}>{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   STEP 2 — PRODUCTS
════════════════════════════════════════════════════════════════════ */
const StepProducts = ({ rows, setRows, allProducts, categories, productType }) => {
  const [openDrop, setOpenDrop] = useState(null);
  const [dropUp, setDropUp] = useState({});
  const [rowQuery, setRowQuery] = useState({});
  const [categoryFilter, setCategoryFilter] = useState(null);
  const containerRef = useRef(null);
  const cardRefs = useRef({});

  useEffect(() => {
    const h = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpenDrop(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = (rowId) => {
    const q = (rowQuery[rowId] || "").toLowerCase();
    let list = allProducts;
    if (categoryFilter) list = list.filter(p => p.category_id === categoryFilter);
    if (!q) return list;
    return list.filter(p => (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q));
  };

  const computeDropDirection = (rowId) => {
    const card = cardRefs.current[rowId];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    setDropUp(prev => ({ ...prev, [rowId]: window.innerHeight - rect.bottom < 290 }));
  };

  const openDropdown = (rowId) => { computeDropDirection(rowId); setOpenDrop(rowId); };

  const selectProd = async (rowId, prod) => {
    let customDefs = [];
    if (productType === "customise") {
      try {
        const res = await customizationsApi.getByProduct(prod.id);
        if (res?.success) customDefs = res.data || [];
      } catch (err) {
        console.error("Failed to fetch customizations", err);
      }
    }
    setRows(r => r.map(row => row._id === rowId
      ? { ...row, productId: prod.id, name: prod.name, sku: prod.sku, price: "", minPrice: prod.min_price, customizations: customDefs, customizationValues: {} }
      : row
    ));
    setRowQuery(q => ({ ...q, [rowId]: prod.name }));
    setOpenDrop(null);
  };

  const updateRow = (id, field, val) => setRows(r => r.map(row => row._id === id ? { ...row, [field]: val } : row));
  const removeRow = (id) => setRows(r => r.length > 1 ? r.filter(row => row._id !== id) : r);

  const validRows = rows.filter(r => r.productId && Number(r.qty) > 0);
  const grandTotal = rows.reduce((s, r) => s + (Number(r.qty) || 0) * (parseFloat(r.price) || 0), 0);

  return (
    <div className="po-step-body">
      <div className="po-step-title"><Package size={18} /> Add Products to Order</div>
      <p className="po-step-desc">Select products from the catalog. You can override the unit price if needed.</p>

      {categories && categories.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <select
            value={categoryFilter ?? ""}
            onChange={e => setCategoryFilter(e.target.value ? parseInt(e.target.value, 10) : null)}
            style={{ fontSize: "0.82rem", padding: "7px 12px", borderRadius: 8, border: "1.5px solid var(--border)", background: "#fff", color: "var(--text)", cursor: "pointer", minWidth: 200 }}
          >
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      <div className="po-prod-list" ref={containerRef}>
        {rows.map((row, idx) => {
          const lineTotal = (parseFloat(row.price) || 0) * (Number(row.qty) || 0);
          const opts = filtered(row._id);
          const displayVal = rowQuery[row._id] ?? row.name;

          return (
            <div
              key={row._id}
              className={`po-prod-card ${row.productId ? "po-prod-card--selected" : ""}`}
              ref={el => { cardRefs.current[row._id] = el; }}
            >
              <div className="po-prod-card-search" style={{ position: "relative" }}>
                <div className="po-prod-card-index">{idx + 1}</div>
                <div className={`po-prod-input-wrap ${row.productId ? "po-prod-input-wrap--selected" : ""}`}>
                  <Search size={14} className="po-prod-search-ico" />
                  <input
                    className="po-prod-input"
                    value={displayVal}
                    placeholder="Search product by name or SKU…"
                    onChange={e => {
                      setRowQuery(q => ({ ...q, [row._id]: e.target.value }));
                      setRows(r => r.map(ro => ro._id === row._id ? { ...ro, productId: null, name: e.target.value, sku: "" } : ro));
                      openDropdown(row._id);
                    }}
                    onFocus={() => openDropdown(row._id)}
                  />
                  {row.productId && <span className="po-prod-selected-badge"><Check size={11} /> Selected</span>}
                </div>

                {openDrop === row._id && opts.length > 0 && (
                  <div className={`po-prod-dropdown${dropUp[row._id] ? " po-prod-dropdown--up" : ""}`}>
                    {opts.slice(0, 40).map(p => (
                      <div key={p.id} className="po-prod-option" onMouseDown={() => selectProd(row._id, p)}>
                        <div className="po-prod-opt-left">
                          <div className="po-prod-opt-name">{p.name}</div>
                          {p.sku && <span className="po-prod-opt-sku"><Tag size={9} />{p.sku}</span>}
                        </div>
                        <span className="po-prod-opt-price">₹{p.min_price?.toLocaleString("en-IN") || "—"}</span>
                      </div>
                    ))}
                    {opts.length > 40 && <div className="po-prod-more">+{opts.length - 40} more — type to narrow</div>}
                  </div>
                )}
              </div>

              <div
                className="po-prod-card-meta"
                style={productType === "customise" && row.productId && row.customizations?.length > 0 ? { borderRadius: 0, borderBottom: "1.5px solid var(--border-2)" } : {}}
              >
                <div className="po-prod-card-sku">
                  {row.sku ? <span className="po-sku-tag"><Tag size={10} />{row.sku}</span> : <span className="po-sku-empty">No SKU</span>}
                </div>

                <div className="po-prod-card-fields">
                  <div className="po-prod-field-group">
                    <label className="po-prod-field-label">Unit Price</label>
                    <div className="po-price-input-wrap">
                      <span className="po-price-prefix">₹</span>
                      <input
                        className={`po-num-input ${row.productId && row.price !== "" && parseFloat(row.price) < row.minPrice ? "input-error" : ""}`}
                        type="number" min="0" step="0.01"
                        value={row.price}
                        placeholder={row.minPrice ? `min ₹${row.minPrice}` : "0.00"}
                        onChange={e => updateRow(row._id, "price", e.target.value)}
                      />
                    </div>
                    {row.productId && row.price !== "" && parseFloat(row.price) < row.minPrice && (
                      <span style={{ color: "#ef4444", fontSize: "11px", display: "flex", alignItems: "center", gap: "3px", marginTop: "4px" }}>
                        <AlertCircle size={10} /> Below min: ₹{row.minPrice}
                      </span>
                    )}
                  </div>

                  <div className="po-prod-field-group">
                    <label className="po-prod-field-label">Qty</label>
                    <input
                      className="po-num-input po-qty-input"
                      type="number" min="1"
                      value={row.qty}
                      placeholder="Qty"
                      onChange={e => updateRow(row._id, "qty", parseInt(e.target.value))}
                    />
                  </div>

                  <div className="po-prod-field-group">
                    <label className="po-prod-field-label">Line Total</label>
                    <div className="po-line-total">
                      {parseFloat(row.price) > 0
                        ? <strong>₹{lineTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
                        : <span className="po-line-total-empty">—</span>}
                    </div>
                  </div>

                  {rows.length > 1 && (
                    <button className="po-remove-btn" onClick={() => removeRow(row._id)} title="Remove">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>

              {productType === "customise" && row.productId && row.customizations?.length > 0 && (
                <div style={{ padding: "16px", background: "#fafafa", display: "flex", flexDirection: "column", gap: "12px", borderRadius: "0 0 9px 9px" }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Settings size={12} /> Customization Specifications
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "12px" }}>
                    {[...(row.customizations || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(a => {
                      const cust = a.customization;
                      if (!cust) return null;
                      const val = row.customizationValues?.[cust.id] || "";
                      const setVal = (newVal) => setRows(rs => rs.map(r => r._id === row._id ? { ...r, customizationValues: { ...(r.customizationValues || {}), [cust.id]: newVal } } : r));
                      const isMissing = cust.is_required && (!val || (typeof val === "string" && !val.trim()));
                      return (
                        <div key={cust.id} className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontSize: "0.78rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                            {cust.label} {cust.is_required && <span style={{ color: "var(--danger)" }}>*</span>}
                            {isMissing && <span style={{ color: "var(--danger)", fontSize: "0.7rem" }}>(Required)</span>}
                          </label>
                          {cust.input_type === "text" && <input className={`form-input ${isMissing ? "input-error" : ""}`} type="text" value={val} placeholder={cust.placeholder || "Enter text..."} onChange={e => setVal(e.target.value)} style={{ fontSize: "0.82rem", padding: "8px 10px" }} />}
                          {cust.input_type === "textarea" && <textarea className={`form-input ${isMissing ? "input-error" : ""}`} value={val} placeholder={cust.placeholder || "Enter details..."} onChange={e => setVal(e.target.value)} style={{ fontSize: "0.82rem", padding: "8px 10px", minHeight: "60px", resize: "vertical" }} />}
                          {cust.input_type === "number" && (
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <input className={`form-input ${isMissing ? "input-error" : ""}`} type="number" value={val} min={cust.min_value ?? undefined} max={cust.max_value ?? undefined} placeholder={cust.placeholder || "0"} onChange={e => setVal(e.target.value)} style={{ fontSize: "0.82rem", padding: "8px 10px", flex: 1 }} />
                              {cust.unit && <span className="mono" style={{ fontSize: "0.75rem", background: "#f1f5f9", padding: "8px 10px", borderRadius: "4px", border: "1.5px solid #e2e8f0", borderLeft: "none", marginLeft: "-6px" }}>{cust.unit}</span>}
                            </div>
                          )}
                          {cust.input_type === "select" && (
                            <select className={`form-input ${isMissing ? "input-error" : ""}`} value={val} onChange={e => setVal(e.target.value)} style={{ fontSize: "0.82rem", padding: "8px 10px" }}>
                              <option value="">— Select —</option>
                              {(() => {
                                if (Array.isArray(cust.options)) return cust.options;
                                if (typeof cust.options === "string") { try { const p = JSON.parse(cust.options); if (Array.isArray(p)) return p; } catch { return cust.options.split(",").map(o => o.trim()).filter(Boolean); } }
                                return [];
                              })().map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          )}
                          {cust.input_type === "color" && (
                            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                              <input type="color" value={val.startsWith("#") && val.length === 7 ? val : "#6366f1"} onChange={e => setVal(e.target.value)} style={{ width: 32, height: 32, padding: 0, border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", flexShrink: 0 }} />
                              <input className={`form-input mono ${isMissing ? "input-error" : ""}`} type="text" value={val} placeholder="#HEX or Color name..." onChange={e => setVal(e.target.value)} style={{ fontSize: "0.82rem", padding: "8px 10px", flex: 1 }} />
                            </div>
                          )}
                          {cust.input_type === "media" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                <label className="po-loc-add-btn" style={{ margin: 0, padding: "8px 12px", cursor: "pointer", fontSize: "0.78rem" }}>
                                  <Upload size={12} /> Choose File
                                  <input type="file" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) { if (f.size > 5 * 1024 * 1024) { alert("Max 5MB"); return; } setVal(f); } }} />
                                </label>
                                <span style={{ fontSize: "0.78rem", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{val instanceof File ? val.name : (val || "No file selected")}</span>
                                {val && <button type="button" onClick={() => setVal("")} style={{ background: "none", border: "none", color: "var(--danger)", padding: "4px", cursor: "pointer" }}><X size={12} /></button>}
                              </div>
                              {cust.reference_logo && <a href={cust.reference_logo} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "0.7rem", color: "var(--primary)" }}><FileText size={10} /> View Reference</a>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button className="po-add-row-btn" onClick={() => setRows(r => [...r, emptyRow()])}>
        <Plus size={14} /> Add Another Product
      </button>

      {validRows.length > 0 && (
        <div className="po-total-bar">
          <div className="po-total-bar-left"><ShoppingBag size={16} /><span>{validRows.length} item{validRows.length !== 1 ? "s" : ""} selected</span></div>
          <div className="po-total-bar-right">
            <span>Grand Total</span>
            <span className="po-grand-total"><IndianRupee size={15} strokeWidth={2.5} />{grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   STEP 3 — DELIVERY LOCATION (regular mode)
════════════════════════════════════════════════════════════════════ */
const StepLocation = ({ locations, loading, selected, onSelect, lastOrderedAddress, onRestoreLast, searchQuery, setSearchQuery, page, setPage, totalPages, onAddLocation }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [addingLoading, setAddingLoading] = useState(false);
  const [form, setForm] = useState({ address_line_1: "", address_line_2: "", city: "", state: "", pincode: "", country: "India", is_primary: false });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.address_line_1.trim()) errs.address_line_1 = "Address Line 1 is required.";
    if (!form.city.trim()) errs.city = "City is required.";
    if (!form.state.trim()) errs.state = "State is required.";
    return errs;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setAddingLoading(true);
    try {
      await onAddLocation(form);
      setForm({ address_line_1: "", address_line_2: "", city: "", state: "", pincode: "", country: "India", is_primary: false });
      setErrors({});
      setIsAdding(false);
    } catch { /* parent toasts */ } finally { setAddingLoading(false); }
  };

  return (
    <div className="po-step-body">
      <div className="po-loc-header-row">
        <div className="po-step-title" style={{ margin: 0 }}>
          <MapPin size={18} /> Select Delivery Location
        </div>
        <button className="po-loc-add-btn" type="button" onClick={() => { setIsAdding(!isAdding); setErrors({}); }}>
          {isAdding ? <X size={14} /> : <Plus size={14} />}
          {isAdding ? "Cancel" : "Add New Address"}
        </button>
      </div>
      <p className="po-step-desc">Select a saved delivery address or skip to proceed.</p>

      {lastOrderedAddress && !isAdding && (
        <div className="po-loc-last-banner">
          <div className="po-loc-banner-left">
            <MapPin size={18} className="po-loc-banner-icon" />
            <div>
              <div className="po-loc-banner-title">Previous Order Delivery Details Found</div>
              <div className="po-loc-banner-details">
                {lastOrderedAddress.mode_of_delivery && <span><strong>Mode:</strong> {lastOrderedAddress.mode_of_delivery}</span>}
                {lastOrderedAddress.delivery_person && <span><strong>Person:</strong> {lastOrderedAddress.delivery_person}</span>}
                {lastOrderedAddress.delivery_details && <span><strong>Details:</strong> {lastOrderedAddress.delivery_details}</span>}
              </div>
            </div>
          </div>
          <button className="po-loc-banner-btn" type="button" onClick={onRestoreLast}>Use These Details</button>
        </div>
      )}

      {isAdding && (
        <form onSubmit={handleSave} className="po-loc-form-card">
          <div className="po-loc-form-title"><Plus size={16} /> Add New Delivery Address</div>
          <div className="po-loc-form-grid">
            <div className="po-loc-form-group po-loc-form-full">
              <label className="po-loc-form-label">Address Line 1 *</label>
              <input className={`po-loc-form-input ${errors.address_line_1 ? "input-error" : ""}`} value={form.address_line_1} onChange={e => { setForm(f => ({ ...f, address_line_1: e.target.value })); setErrors(er => ({ ...er, address_line_1: "" })); }} placeholder="Flat/House No., Street, Area details..." />
              {errors.address_line_1 && <span className="po-loc-error-msg">{errors.address_line_1}</span>}
            </div>
            <div className="po-loc-form-group po-loc-form-full">
              <label className="po-loc-form-label">Address Line 2 (Optional)</label>
              <input className="po-loc-form-input" value={form.address_line_2} onChange={e => setForm(f => ({ ...f, address_line_2: e.target.value }))} placeholder="Landmark, Suite, Unit, etc." />
            </div>
            <div className="po-loc-form-group">
              <label className="po-loc-form-label">City *</label>
              <input className={`po-loc-form-input ${errors.city ? "input-error" : ""}`} value={form.city} onChange={e => { setForm(f => ({ ...f, city: e.target.value })); setErrors(er => ({ ...er, city: "" })); }} placeholder="Enter city" />
              {errors.city && <span className="po-loc-error-msg">{errors.city}</span>}
            </div>
            <div className="po-loc-form-group">
              <label className="po-loc-form-label">State *</label>
              <input className={`po-loc-form-input ${errors.state ? "input-error" : ""}`} value={form.state} onChange={e => { setForm(f => ({ ...f, state: e.target.value })); setErrors(er => ({ ...er, state: "" })); }} placeholder="Enter state" />
              {errors.state && <span className="po-loc-error-msg">{errors.state}</span>}
            </div>
            <div className="po-loc-form-group">
              <label className="po-loc-form-label">Pincode (Optional)</label>
              <input className="po-loc-form-input" value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} placeholder="6-digit postal code" />
            </div>
            <div className="po-loc-form-group">
              <label className="po-loc-form-label">Country</label>
              <input className="po-loc-form-input" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} placeholder="India" />
            </div>
            <div className="po-loc-form-group po-loc-form-full" style={{ marginTop: 8 }}>
              <label className="po-loc-checkbox-label">
                <input type="checkbox" className="po-loc-checkbox" checked={form.is_primary} onChange={e => setForm(f => ({ ...f, is_primary: e.target.checked }))} />
                Set as primary delivery address
              </label>
            </div>
          </div>
          <div className="po-loc-form-actions">
            <button className="po-loc-page-btn" type="button" disabled={addingLoading} onClick={() => { setIsAdding(false); setErrors({}); }}>Cancel</button>
            <button className="btn-primary po-nav-btn" type="submit" disabled={addingLoading} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {addingLoading ? <><Loader2 size={14} className="spin" /> Saving…</> : <><Check size={14} /> Save Address</>}
            </button>
          </div>
        </form>
      )}

      {!isAdding && (
        <div className="po-loc-search-wrap">
          <Search size={14} className="po-loc-search-ico" />
          <input className="po-loc-search-input" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} placeholder="Search saved locations by address, city, state, or pincode..." />
          {searchQuery && <button className="po-loc-search-clear" type="button" onClick={() => { setSearchQuery(""); setPage(1); }}><X size={12} /></button>}
        </div>
      )}

      {loading ? (
        <div className="po-loc-loading"><Loader2 className="spin" size={24} /><span>Fetching customer locations…</span></div>
      ) : locations.length === 0 ? (
        !isAdding && (
          <div className="po-loc-empty">
            <MapPin size={32} />
            <div className="po-loc-empty-title">{searchQuery ? "No Matching Locations" : "No Saved Locations"}</div>
            <p>{searchQuery ? `No saved addresses match "${searchQuery}".` : `No addresses saved. Click "Add New Address" or skip.`}</p>
          </div>
        )
      ) : !isAdding && (
        <>
          <div className="po-loc-grid">
            {locations.map(loc => {
              const isSel = selected?.id === loc.id;
              return (
                <div key={loc.id} className={`po-loc-card ${isSel ? "po-loc-card--selected" : ""}`} onClick={() => onSelect(loc)}>
                  <div className="po-loc-card-header">
                    <span className="po-loc-card-icon-wrap"><MapPin size={16} /></span>
                    {loc.is_primary && <span className="po-loc-primary-badge">Primary</span>}
                    {isSel && <span className="po-loc-selected-check"><Check size={12} strokeWidth={3} /></span>}
                  </div>
                  <div className="po-loc-card-body">
                    <div className="po-loc-addr-line1">{loc.address_line_1}</div>
                    {loc.address_line_2 && <div className="po-loc-addr-line2">{loc.address_line_2}</div>}
                    <div className="po-loc-city-state">{loc.city && <span>{loc.city}</span>}{loc.state && <span>, {loc.state}</span>}</div>
                    {(loc.pincode || loc.country) && <div className="po-loc-pin-country">{loc.pincode && <span className="mono">{loc.pincode}</span>}{loc.country && <span>, {loc.country}</span>}</div>}
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="po-loc-pagination">
              <button className="po-loc-page-btn" type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><ChevronLeft size={14} /> Previous</button>
              <span className="po-loc-page-indicator">Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>
              <button className="po-loc-page-btn" type="button" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next <ChevronRight size={14} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   BRANCH LOCATION MODAL (org mode)
════════════════════════════════════════════════════════════════════ */
const BranchLocationModal = ({ branch, onClose, onSave }) => {
  const [locations, setLocations] = useState([]);
  const [loadingLocs, setLoadingLocs] = useState(true);
  const [selected, setSelected] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [addingLoading, setAddingLoading] = useState(false);
  const [form, setForm] = useState({ address_line_1: "", address_line_2: "", city: "", state: "", pincode: "", country: "India", is_primary: false });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setLoadingLocs(true);
    leadsApi.getLocations(branch.id)
      .then(res => {
        const d = res || {};
        const arr = Array.isArray(d) ? d : (d.locations || d.data || []);
        setLocations(arr);
        setSelected(arr.find(l => l.is_primary) || arr[0] || null);
      })
      .catch(() => toast.error("Failed to load locations."))
      .finally(() => setLoadingLocs(false));
  }, [branch.id]);

  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: "" })); };

  const validate = () => {
    const errs = {};
    if (!form.address_line_1.trim()) errs.address_line_1 = "Address Line 1 is required.";
    if (!form.city.trim()) errs.city = "City is required.";
    if (!form.state.trim()) errs.state = "State is required.";
    return errs;
  };

  const handleSaveNew = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setAddingLoading(true);
    try {
      const res = await leadsApi.createLocation(branch.id, form);
      const saved = res.data?.data || res.data;
      toast.success("Location added!");
      setLocations(prev => [...prev, saved]);
      setSelected(saved);
      setForm({ address_line_1: "", address_line_2: "", city: "", state: "", pincode: "", country: "India", is_primary: false });
      setErrors({});
      setIsAdding(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save location.");
    } finally {
      setAddingLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selected) { toast.error("Please select a location."); return; }
    onSave({ ...branch, ...selected });
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal pm-modal" style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <div className="pm-modal-title">
            <div className="pm-modal-icon" style={{ background: "#009846" }}><MapPin size={15} strokeWidth={2.5} /></div>
            Delivery Location — {branch.name}
          </div>
          <button className="modal-close" onClick={onClose}><X size={14} /></button>
        </div>

        <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <div className="po-loc-header-row" style={{ marginBottom: 12 }}>
            <span style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>Select a saved address or add a new one.</span>
            <button className="po-loc-add-btn" type="button" onClick={() => { setIsAdding(v => !v); setErrors({}); }}>
              {isAdding ? <X size={14} /> : <Plus size={14} />}
              {isAdding ? "Cancel" : "Add New Address"}
            </button>
          </div>

          {isAdding && (
            <form onSubmit={handleSaveNew} className="po-loc-form-card">
              <div className="po-loc-form-title"><Plus size={16} /> Add New Delivery Address</div>
              <div className="po-loc-form-grid">
                <div className="po-loc-form-group po-loc-form-full">
                  <label className="po-loc-form-label">Address Line 1 *</label>
                  <input className={`po-loc-form-input${errors.address_line_1 ? " input-error" : ""}`} value={form.address_line_1} onChange={e => setField("address_line_1", e.target.value)} placeholder="Flat/House No., Street, Area details..." />
                  {errors.address_line_1 && <span className="po-loc-error-msg">{errors.address_line_1}</span>}
                </div>
                <div className="po-loc-form-group po-loc-form-full">
                  <label className="po-loc-form-label">Address Line 2 (Optional)</label>
                  <input className="po-loc-form-input" value={form.address_line_2} onChange={e => setField("address_line_2", e.target.value)} placeholder="Landmark, Suite, Unit, etc." />
                </div>
                <div className="po-loc-form-group">
                  <label className="po-loc-form-label">City *</label>
                  <input className={`po-loc-form-input${errors.city ? " input-error" : ""}`} value={form.city} onChange={e => setField("city", e.target.value)} placeholder="Enter city" />
                  {errors.city && <span className="po-loc-error-msg">{errors.city}</span>}
                </div>
                <div className="po-loc-form-group">
                  <label className="po-loc-form-label">State *</label>
                  <input className={`po-loc-form-input${errors.state ? " input-error" : ""}`} value={form.state} onChange={e => setField("state", e.target.value)} placeholder="Enter state" />
                  {errors.state && <span className="po-loc-error-msg">{errors.state}</span>}
                </div>
                <div className="po-loc-form-group">
                  <label className="po-loc-form-label">Pincode (Optional)</label>
                  <input className="po-loc-form-input" value={form.pincode} onChange={e => setField("pincode", e.target.value)} placeholder="6-digit postal code" maxLength={6} />
                </div>
                <div className="po-loc-form-group">
                  <label className="po-loc-form-label">Country</label>
                  <input className="po-loc-form-input" value={form.country} onChange={e => setField("country", e.target.value)} placeholder="India" />
                </div>
                <div className="po-loc-form-group po-loc-form-full" style={{ marginTop: 8 }}>
                  <label className="po-loc-checkbox-label">
                    <input type="checkbox" className="po-loc-checkbox" checked={form.is_primary} onChange={e => setField("is_primary", e.target.checked)} />
                    Set as primary delivery address
                  </label>
                </div>
              </div>
              <div className="po-loc-form-actions">
                <button type="button" className="po-loc-page-btn" disabled={addingLoading} onClick={() => { setIsAdding(false); setErrors({}); }}>Cancel</button>
                <button type="submit" className="btn-primary po-nav-btn" disabled={addingLoading} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {addingLoading ? <><Loader2 size={14} className="spin" /> Saving…</> : <><Check size={14} /> Save Address</>}
                </button>
              </div>
            </form>
          )}

          {!isAdding && (
            loadingLocs ? (
              <div className="po-loc-loading"><Loader2 className="spin" size={24} /><span>Fetching locations…</span></div>
            ) : locations.length === 0 ? (
              <div className="po-loc-empty">
                <MapPin size={32} />
                <div className="po-loc-empty-title">No Saved Locations</div>
                <p>No addresses saved for this branch yet. Click "Add New Address" to add one.</p>
              </div>
            ) : (
              <div className="po-loc-grid">
                {locations.map(loc => {
                  const isSel = selected?.id === loc.id;
                  return (
                    <div key={loc.id} className={`po-loc-card${isSel ? " po-loc-card--selected" : ""}`} onClick={() => setSelected(loc)}>
                      <div className="po-loc-card-header">
                        <span className="po-loc-card-icon-wrap"><MapPin size={16} /></span>
                        {loc.is_primary && <span className="po-loc-primary-badge">Primary</span>}
                        {isSel && <span className="po-loc-selected-check"><Check size={12} strokeWidth={3} /></span>}
                      </div>
                      <div className="po-loc-card-body">
                        <div className="po-loc-addr-line1">{loc.address_line_1}</div>
                        {loc.address_line_2 && <div className="po-loc-addr-line2">{loc.address_line_2}</div>}
                        <div className="po-loc-city-state">{loc.city && <span>{loc.city}</span>}{loc.state && <span>, {loc.state}</span>}</div>
                        {(loc.pincode || loc.country) && <div className="po-loc-pin-country">{loc.pincode && <span className="mono">{loc.pincode}</span>}{loc.country && <span>, {loc.country}</span>}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        <div className="pm-footer">
          <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" onClick={handleConfirm} disabled={!selected || isAdding} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Check size={14} /> Confirm Location
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   STEP 3 (ORG MODE) — BRANCH QUANTITIES
════════════════════════════════════════════════════════════════════ */
const StepBranchQty = ({ branches, setBranches, loadingBranches, rows, setRows, branchQty, setBranchQty, orgName }) => {
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchLocations, setBranchLocations] = useState({});
  const [loadingBranchLocs, setLoadingBranchLocs] = useState(false);

  useEffect(() => {
    if (!branches.length) return;
    setLoadingBranchLocs(true);
    Promise.all(
      branches.map(b =>
        leadsApi.getLocations(b.id)
          .then(res => {
            const d = res || {};
            const arr = Array.isArray(d) ? d : (d.locations || d.data || []);
            return { id: b.id, loc: arr.find(l => l.is_primary) || arr[0] || null };
          })
          .catch(() => ({ id: b.id, loc: null }))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.id] = r.loc; });
      setBranchLocations(map);
    }).finally(() => setLoadingBranchLocs(false));
  }, [branches]);

  const validRows = rows.filter(r => r.productId && Number(r.qty) > 0);

  const getQty = (branchId, productId) => (branchQty[branchId]?.[productId] ?? 0);

  const setQty = (branchId, productId, val) => {
    const intVal = Math.max(0, parseInt(val) || 0);
    setBranchQty(prev => {
      const next = { ...prev, [branchId]: { ...(prev[branchId] || {}), [productId]: intVal } };
      let sum = 0;
      branches.forEach(b => { sum += b.id === branchId ? intVal : (next[b.id]?.[productId] ?? 0); });
      setRows(rs => rs.map(r => r.productId === productId ? { ...r, qty: sum } : r));
      return next;
    });
  };

  const handleDistribute = (productId, targetQty) => {
    if (!branches.length) return;
    const base = Math.floor(targetQty / branches.length);
    const rem = targetQty % branches.length;
    setBranchQty(prev => {
      const next = { ...prev };
      branches.forEach((b, i) => {
        if (!next[b.id]) next[b.id] = {};
        next[b.id][productId] = base + (i < rem ? 1 : 0);
      });
      setRows(rs => rs.map(r => r.productId === productId ? { ...r, qty: targetQty } : r));
      return next;
    });
  };

  if (loadingBranches) {
    return (
      <div className="po-step-body">
        <div className="po-step-title"><GitBranch size={18} /> Branch Quantities</div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", padding: "30px 0", color: "var(--text-muted)" }}>
          <Loader2 size={20} className="spin" /> Loading branches for {orgName}…
        </div>
      </div>
    );
  }

  if (validRows.length === 0) {
    return (
      <div className="po-step-body">
        <div className="po-step-title"><GitBranch size={18} /> Branch Quantities</div>
        <div className="empty-state" style={{ border: "1px dashed var(--border)", borderRadius: "var(--radius)", padding: "30px 0" }}>
          <AlertCircle size={24} style={{ color: "var(--text-dim)", opacity: 0.5, marginBottom: 8 }} />
          <p>Please go back and add at least one product first.</p>
        </div>
      </div>
    );
  }

  if (branches.length === 0) {
    return (
      <div className="po-step-body">
        <div className="po-step-title"><GitBranch size={18} /> Branch Quantities</div>
        <div className="empty-state" style={{ border: "1px dashed var(--border)", borderRadius: "var(--radius)", padding: "30px 0" }}>
          <GitBranch size={24} style={{ color: "var(--text-dim)", opacity: 0.5, marginBottom: 8 }} />
          <p>No branches linked to <strong>{orgName}</strong> yet. You can proceed — the order will be placed for the organization only.</p>
        </div>
      </div>
    );
  }

  const grandTotal = branches.reduce((sum, b) =>
    sum + validRows.reduce((s, r) => s + getQty(b.id, r.productId) * (parseFloat(r.price) || 0), 0), 0);

  return (
    <div className="po-step-body">
      <div className="po-step-title"><GitBranch size={18} /> Branch Quantities</div>
      <p className="po-step-desc">Enter the quantity needed for each branch. Leave as 0 to skip a branch for a product.</p>

      <div className="table-wrap" style={{ border: "1px solid var(--border)", borderRadius: "var(--radius)", overflowX: "auto" }}>
        <table className="data-table" style={{ minWidth: "600px" }}>
          <thead>
            <tr>
              <th style={{ minWidth: 180 }}>Branch Name</th>
              <th style={{ minWidth: 200 }}>Address</th>
              {validRows.map(r => {
                const allocated = branches.reduce((sum, b) => sum + getQty(b.id, r.productId), 0);
                const limit = r.fixedQty ?? r.qty;
                const isOver = allocated > limit;
                const isMatched = allocated === limit;
                return (
                  <th key={r._id} style={{ textAlign: "center", minWidth: 140, verticalAlign: "top" }}>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 4, whiteSpace: "normal", wordBreak: "break-word" }}>{r.name}</div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <span style={{ fontSize: "0.7rem", padding: "2px 6px", borderRadius: 4, fontWeight: 600, background: isOver ? "rgba(239,68,68,0.1)" : isMatched ? "rgba(22,163,74,0.1)" : "rgba(234,179,8,0.1)", color: isOver ? "#ef4444" : isMatched ? "#16a34a" : "#ca8a04", border: `1px solid ${isOver ? "rgba(239,68,68,0.25)" : isMatched ? "rgba(22,163,74,0.25)" : "rgba(234,179,8,0.25)"}` }}>
                        {isOver ? `Over: ${allocated}/${limit}` : `${allocated}/${limit}`}
                      </span>
                      <button type="button" onClick={() => handleDistribute(r.productId, limit)}
                        style={{ background: "var(--bg-3)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--primary)", fontSize: "0.68rem", cursor: "pointer", padding: "2px 6px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}>
                        Divide Equally
                      </button>
                    </div>
                  </th>
                );
              })}
              <th style={{ textAlign: "right", minWidth: 110 }}>Branch Total</th>
            </tr>
          </thead>
          <tbody>
            {branches.map(b => {
              const branchTotal = validRows.reduce((s, r) => s + getQty(b.id, r.productId) * (parseFloat(r.price) || 0), 0);
              const loc = branchLocations[b.id];
              const addrParts = loc ? [loc.address_line_1, loc.address_line_2, loc.city, loc.state, loc.pincode].filter(Boolean) : [];
              const addrText = addrParts.length ? addrParts.join(", ") : "—";
              return (
                <tr key={b.id}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: "0.87rem" }}>{b.name}</div>
                    {b.shop_name && <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{b.shop_name}</div>}
                  </td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", gap: 5, alignItems: "flex-start", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                        {loadingBranchLocs ? <Loader2 size={11} className="spin" style={{ marginTop: 2, flexShrink: 0 }} /> : <MapPin size={11} style={{ marginTop: 2, flexShrink: 0 }} />}
                        <span>{loadingBranchLocs ? "Loading…" : addrText}</span>
                      </div>
                      <button type="button" onClick={() => setEditingBranch(b)}
                        style={{ alignSelf: "flex-start", background: "none", border: "1px solid var(--border)", borderRadius: 4, color: "var(--primary)", fontSize: "0.68rem", fontWeight: 600, cursor: "pointer", padding: "2px 7px", display: "inline-flex", alignItems: "center", gap: 3, fontFamily: "inherit" }}>
                        <MapPin size={9} /> {addrText === "—" ? "Add Location" : "Change"}
                      </button>
                    </div>
                  </td>
                  {validRows.map(r => (
                    <td key={r._id} style={{ textAlign: "center" }}>
                      <input type="number" min="0" value={getQty(b.id, r.productId) || ""}
                        onChange={e => setQty(b.id, r.productId, e.target.value)}
                        style={{ width: 70, textAlign: "center", padding: "6px 8px", border: `1.5px solid ${getQty(b.id, r.productId) > 0 ? "var(--primary)" : "var(--border-2)"}`, borderRadius: "var(--radius-sm)", fontSize: "0.88rem", fontWeight: 600, background: getQty(b.id, r.productId) > 0 ? "rgba(99,102,241,0.05)" : "#fff" }} />
                    </td>
                  ))}
                  <td style={{ textAlign: "right", fontWeight: 700 }}>
                    {branchTotal > 0 ? `₹${branchTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : <span style={{ color: "var(--text-dim)" }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {validRows.some(r => branches.reduce((sum, b) => sum + getQty(b.id, r.productId), 0) > (r.fixedQty ?? r.qty)) && (
        <p style={{ color: "#ef4444", marginTop: 12, fontWeight: 600, fontSize: "0.85rem" }}>
          ⚠ Total allocated quantity cannot exceed the fixed quantity limit. Please adjust branch quantities.
        </p>
      )}

      {grandTotal > 0 && (
        <div className="po-total-bar" style={{ marginTop: 16 }}>
          <div className="po-total-bar-left"><GitBranch size={16} /><span>{branches.filter(b => validRows.some(r => getQty(b.id, r.productId) > 0)).length} branch(es) with items</span></div>
          <div className="po-total-bar-right">
            <span>Grand Total</span>
            <span className="po-grand-total"><IndianRupee size={15} strokeWidth={2.5} />{grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
      )}

      {editingBranch && (
        <BranchLocationModal
          branch={editingBranch}
          onClose={() => setEditingBranch(null)}
          onSave={updated => {
            setBranches(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
            setBranchLocations(prev => ({ ...prev, [updated.id]: updated }));
          }}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   STEP 4 — REVIEW & CONFIRM
════════════════════════════════════════════════════════════════════ */
const StepReview = ({ customer, rows, selectedLocation, productType, isOrgMode, branches, branchQty, invoiceMode, setInvoiceMode, deliveryDate, setDeliveryDate, onGoToStep, onSubmit, placing }) => {
  const [cgstRate, setCgstRate] = useState(0);
  const [sgstRate, setSgstRate] = useState(0);
  const [gstLoading, setGstLoading] = useState(true);

  useEffect(() => {
    crmApiInstance.get("/admin/system-settings")
      .then(res => {
        const settings = res.data?.data || res.data || [];
        const find = (key) => { const s = settings.find(s => s.key === key); return s ? parseFloat(s.value) || 0 : 0; };
        setCgstRate(find("cgst_rate"));
        setSgstRate(find("sgst_rate"));
      })
      .catch(() => { })
      .finally(() => setGstLoading(false));
  }, []);

  const validRows = rows.filter(r => r.productId && Number(r.qty) > 0);
  const subTotal = validRows.reduce((s, r) => s + Number(r.qty) * (parseFloat(r.price) || 0), 0);
  const cgstAmount = (subTotal * cgstRate) / 100;
  const sgstAmount = (subTotal * sgstRate) / 100;
  const gstAmount = cgstAmount + sgstAmount;
  const finalTotal = subTotal + gstAmount;
  const totalGstRate = cgstRate + sgstRate;

  const catLabel = (pt) => {
    if (pt === "printed") return "Printed";
    if (pt === "retail_big") return "Retail Big";
    if (pt === "inter_state_retail") return "Inter-State Retail";
    return "Local Retail";
  };

  return (
    <div className="po-step-body">
      <div className="po-step-title"><FileCheck size={18} /> Review &amp; Place Order</div>
      <p className="po-review-subtitle">Please review your order details before placing the order.</p>

      <div className="po-review-layout">
        {/* ── Left column ── */}
        <div className="po-review-left">
          {/* Customer card */}
          <div className="po-review-card">
            <div className="po-review-card-header">
              <div className="po-review-card-title"><User size={14} /> {isOrgMode ? "Organization" : "Customer"}</div>
              <button className="po-review-edit-btn" onClick={() => onGoToStep(0)}><Pencil size={12} /> Edit</button>
            </div>
            <div className="po-review-customer-row">
              <div className="po-review-avatar">{(customer?.name?.[0] || "?").toUpperCase()}</div>
              <div>
                <div className="po-review-cust-name">{customer?.name}</div>
                <div className="po-review-cust-meta">
                  {customer?.phone && <span>{customer.phone}</span>}
                  {customer?.gst_number && <span>GST: {customer.gst_number}</span>}
                  {customer?.email && <span>{customer.email}</span>}
                  {isOrgMode && branches?.length > 0 && <span>{branches.length} branch(es)</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Location card */}
          {!isOrgMode && selectedLocation && (
            <div className="po-review-card">
              <div className="po-review-card-header">
                <div className="po-review-card-title">
                  <MapPin size={14} /> Delivery Location
                  {selectedLocation.is_primary && <span className="po-review-primary-badge">PRIMARY</span>}
                </div>
                <button className="po-review-edit-btn" onClick={() => onGoToStep(2)}><Pencil size={12} /> Edit</button>
              </div>
              <div className="po-review-loc-addr">{[selectedLocation.address_line_1, selectedLocation.address_line_2].filter(Boolean).join(", ")}</div>
              <div className="po-review-loc-sub">{[selectedLocation.city, selectedLocation.state, selectedLocation.pincode, selectedLocation.country].filter(Boolean).join(", ")}</div>
            </div>
          )}

          {/* Order Items card */}
          <div className="po-review-card">
            <div className="po-review-card-header">
              <div className="po-review-card-title">
                <Package size={14} /> Order Items
                <span className="po-review-item-count">{validRows.length} item{validRows.length !== 1 ? "s" : ""}</span>
              </div>
              <button className="po-review-edit-btn" onClick={() => onGoToStep(1)}><Pencil size={12} /> Edit</button>
            </div>
            <div className="po-review-table-wrap">
              <table className="po-review-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th style={{ textAlign: "right" }}>Unit Price</th>
                    <th style={{ textAlign: "center" }}>Qty</th>
                    <th style={{ textAlign: "right" }}>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {validRows.map(r => (
                    <tr key={r._id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                          <div className="po-review-prod-icon"><Package size={14} /></div>
                          <div>
                            <div className="fw-600" style={{ marginBottom: "4px" }}>{r.name}</div>
                            <span className="po-review-type-badge">{catLabel(productType)}</span>
                            {productType === "printed" && r.customizations?.length > 0 && (
                              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                                {r.customizations.map(a => {
                                  const cust = a.customization;
                                  if (!cust) return null;
                                  const val = r.customizationValues?.[cust.id];
                                  if (!val) return null;
                                  return <div key={cust.id} style={{ fontSize: "0.76rem", color: "var(--text-muted)", display: "flex", gap: 4 }}><span style={{ fontWeight: 600 }}>{cust.label}:</span><span>{val instanceof File ? val.name : String(val)}</span></div>;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td><span className="po-sku-tag"><Tag size={9} />{r.sku || "—"}</span></td>
                      <td style={{ textAlign: "right" }}>₹ {parseFloat(r.price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td style={{ textAlign: "center" }}>{r.qty}</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>₹ {(Number(r.qty) * parseFloat(r.price)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Invoice mode — org orders only */}
          {isOrgMode && (
            <div className="po-review-card">
              <div className="po-review-card-header">
                <div className="po-review-card-title"><FileText size={14} /> Invoice Generation</div>
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {[
                  { value: "per_lead", icon: GitBranch, label: "Separate per Branch", desc: "Each branch gets its own invoice & PDF" },
                  { value: "combined", icon: Building2, label: "Merged Org Invoice", desc: "One invoice for all branches combined" },
                ].map(({ value, icon: Icon, label, desc }) => {
                  const sel = invoiceMode === value;
                  return (
                    <label key={value} onClick={() => setInvoiceMode(value)}
                      style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", padding: "12px 16px", border: `2px solid ${sel ? "var(--primary)" : "var(--border-2)"}`, borderRadius: "8px", flex: 1, minWidth: "200px", background: sel ? "rgba(var(--primary-rgb),0.04)" : "#fff" }}>
                      <Icon size={18} style={{ color: sel ? "var(--primary)" : "var(--text-muted)", flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{label}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{desc}</div>
                      </div>
                      {sel && <Check size={14} style={{ color: "var(--primary)", flexShrink: 0 }} />}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Right column: Order Summary ── */}
        <div className="po-review-sidebar">
          <div className="po-review-summary-card">
            <div className="po-review-summary-title"><ClipboardList size={16} /> Order Summary</div>

            <div className="po-review-summary-rows">
              <div className="po-review-summary-row">
                <span>Sub-Total ({validRows.length} item{validRows.length !== 1 ? "s" : ""})</span>
                <span>₹ {subTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              {!gstLoading && totalGstRate > 0 ? (
                <>
                  <div className="po-review-summary-row" style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>
                    <span style={{ fontStyle: "italic" }}>CGST @{cgstRate}%</span>
                    <span>₹ {cgstAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="po-review-summary-row" style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>
                    <span style={{ fontStyle: "italic" }}>SGST @{sgstRate}%</span>
                    <span>₹ {sgstAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                </>
              ) : !gstLoading && (
                <div className="po-review-summary-row" style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>
                  <span style={{ fontStyle: "italic" }}>GST</span>
                  <span>₹ 0.00</span>
                </div>
              )}
              <div className="po-review-summary-row">
                <span className="po-review-summary-label-icon">Delivery Charges <Info size={13} className="po-review-info-icon" /></span>
                <span>₹ 0.00</span>
              </div>
            </div>

            <div className="po-review-summary-divider" />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <div className="po-review-order-total-label" style={{ marginBottom: 2 }}>Final Total {totalGstRate > 0 ? "(incl. GST)" : ""}</div>
                {!gstLoading && totalGstRate > 0 && (
                  <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "2px 7px", borderRadius: 10, background: "rgba(22,163,74,0.1)", color: "#16a34a", border: "1px solid rgba(22,163,74,0.25)" }}>
                    GST Confirmed ({totalGstRate}%)
                  </span>
                )}
              </div>
              <span className="po-review-order-total-val">₹ {finalTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.75rem", fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>
                <CalendarDays size={13} style={{ color: "var(--primary)" }} />
                Delivery Date <span style={{ fontSize: "0.68rem", fontWeight: 500, color: "var(--text-muted)" }}>(optional)</span>
              </label>
              <input type="date" value={deliveryDate} min={new Date().toISOString().split("T")[0]} onChange={e => setDeliveryDate(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", border: "1.5px solid var(--border-2)", borderRadius: "var(--radius-sm, 8px)", fontSize: "0.88rem", background: "var(--bg, #fff)", color: "var(--text)", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
            </div>

            <div className="po-review-info-box">
              <Info size={14} style={{ flexShrink: 0 }} />
              <span>You can review and confirm your order.</span>
            </div>

            <button className="po-review-place-btn" onClick={onSubmit} disabled={placing || validRows.length === 0}
              style={{ width: "100%", padding: "15px 20px", fontSize: "1rem", fontWeight: 800, letterSpacing: "0.02em", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              {placing ? <><Loader2 size={15} className="spin" /> Placing Order…</> : <><Lock size={15} /> Place Order</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════════════ */
export default function PlaceOrderPage({ onBack, onOrderPlaced, user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbedded = !!onBack;

  const leadIdParam = searchParams.get("lead_id");
  const orgIdParam = searchParams.get("org_id");
  const orgNameParam = searchParams.get("org_name") || "";
  const isOrgMode = !!orgIdParam;
  const activeSteps = isOrgMode ? STEPS_ORG : STEPS_REGULAR;

  const [step, setStep] = useState(0);
  const [productType, setProductType] = useState("");
  const [clientType, setClientType] = useState("Place Order");
  const [salesExec, setSalesExec] = useState("");
  const [allSalesStaff, setAllSalesStaff] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [rows, setRows] = useState([emptyRow()]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [placing, setPlacing] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");

  /* Fetch all users for Sales Executive picker */
  useEffect(() => {
    usersApi.getAll({ limit: 200 })
      .then(res => {
        const data = res.data || res;
        const arr = Array.isArray(data) ? data : (data?.rows || data?.data || []);
        setAllSalesStaff(arr);
      })
      .catch(() => { });
  }, []);

  /* Org mode state */
  const [invoiceMode, setInvoiceMode] = useState("per_lead");
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [branchQty, setBranchQty] = useState({});


  const [lastOrder, setLastOrder] = useState(null);
  const [loadingLastOrder, setLoadingLastOrder] = useState(false);

  /* Load branches for org mode */
  useEffect(() => {
    if (!isOrgMode) return;
    setLoadingBranches(true);
    leadsApi.getAll({ organisation_id: orgIdParam, limit: 200 })
      .then(res => {
        // leadsApi.getAll already extracts r.data (JSON body), so res = {success, data:[...], count}
        const arr = res.data ?? res;
        setBranches(Array.isArray(arr) ? arr : []);
      })
      .catch(() => toast.error("Failed to load branches for this organization."))
      .finally(() => setLoadingBranches(false));
  }, [isOrgMode, orgIdParam]);

  /* Auto-distribute qty when entering branch qty step */
  useEffect(() => {
    if (!isOrgMode || step !== 2 || !branches.length) return;
    const validRows = rows.filter(r => r.productId && Number(r.qty) > 0);
    setBranchQty(prev => {
      const next = { ...prev };
      let updated = false;
      validRows.forEach(r => {
        const currentSum = branches.reduce((s, b) => s + (next[b.id]?.[r.productId] ?? 0), 0);
        if (currentSum === 0 && r.qty > 0) {
          const base = Math.floor(r.qty / branches.length);
          const rem = r.qty % branches.length;
          branches.forEach((b, i) => {
            if (!next[b.id]) next[b.id] = {};
            next[b.id][r.productId] = base + (i < rem ? 1 : 0);
          });
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  }, [step, isOrgMode, branches]);

  /* Pre-load product catalog */
  useEffect(() => {
    productsApi.getAll({ limit: 500, sortBy: "name", sortOrder: "ASC" })
      .then(res => {
        const arr = res.data?.data || res.data || [];
        setAllProducts(Array.isArray(arr) ? arr.filter(p => p.is_active !== false).map(p => ({ id: p.id, name: p.name || "", sku: p.sku || "", min_price: parseFloat(p.min_price || 0), category_id: p.category_id ?? null })) : []);
      })
      .catch(() => toast.error("Could not load product catalog"));
    productsApi.getCategories()
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  /* Auto-select customer from lead_id param */
  useEffect(() => {
    if (!leadIdParam) return;
    leadsApi.getById(leadIdParam)
      .then(res => {
        const lead = res.data?.data || res.data;
        if (lead) setCustomer({ id: lead.id, name: lead.name, phone: lead.phone || "", email: lead.email || "", gst_number: lead.gst_number || "" });
      })
      .catch(() => toast.error("Failed to fetch lead details."));
  }, [leadIdParam]);

  /* Location step state */
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [locSearch, setLocSearch] = useState("");
  const [locPage, setLocPage] = useState(1);
  const [locTotalPages, setLocTotalPages] = useState(1);
  const [lastOrderedAddress, setLastOrderedAddress] = useState(null);
  const [newlyAddedLocId, setNewlyAddedLocId] = useState(null);
  const debLocSearch = useDebounce(locSearch, 350);

  /* Reset when customer changes */
  useEffect(() => {
    setLastOrder(null);
    setSelectedLocation(null);
    setLocations([]);
    setLastOrderedAddress(null);
    setLocPage(1);
  }, [customer]);

  /* Fetch last order for quick reorder */
  useEffect(() => {
    if (!customer?.id || isOrgMode) { setLastOrder(null); return; }
    setLoadingLastOrder(true);
    leadsApi.getOrders(customer.id, { page: 1, limit: 1 })
      .then(res => {
        const orders = res.data?.data || res.data || [];
        const last = Array.isArray(orders) ? (orders[0] || null) : null;
        setLastOrder(last);
        if (last?.client_type) setClientType(last.client_type);
      })
      .catch(() => setLastOrder(null))
      .finally(() => setLoadingLastOrder(false));
  }, [customer, isOrgMode]);

  /* Fetch customer locations for Location step */
  useEffect(() => {
    if (!customer?.id || isOrgMode) { setLocations([]); setLocTotalPages(1); return; }
    setLocLoading(true);
    leadsApi.getLocations(customer.id, { page: locPage, limit: 10, search: debLocSearch })
      .then(res => {
        const locs = res.data?.locations || res.locations || [];
        const lastAddr = res.data?.last_ordered_address || res.last_ordered_address || null;
        const totalP = res.data?.totalPages || res.totalPages || 1;
        setLocations(locs);
        setLastOrderedAddress(lastAddr);
        setLocTotalPages(totalP);
        setSelectedLocation(prev => {
          if (newlyAddedLocId) {
            const added = locs.find(l => l.id === newlyAddedLocId);
            if (added) { setNewlyAddedLocId(null); return added; }
          }
          if (prev) return prev;
          const primary = locs.find(l => l.is_primary) || locs[0] || null;
          if (lastAddr) {
            const matched = locs.find(l => formatAddress(l) === lastAddr.delivery_details);
            return matched || primary;
          }
          return primary;
        });
      })
      .catch(() => toast.error("Failed to fetch customer locations"))
      .finally(() => setLocLoading(false));
  }, [customer, isOrgMode, debLocSearch, locPage, newlyAddedLocId]);

  const handleAddLocation = async (form) => {
    if (!customer?.id) return;
    const res = await leadsApi.createLocation(customer.id, form);
    const newLoc = res.data?.data || res.data;
    if (newLoc?.id) setNewlyAddedLocId(newLoc.id);
    toast.success("Location added!");
    setLocPage(1);
    setLocSearch("");
  };


  /* Auto-load customizations when switching to customise type */
  useEffect(() => {
    if (productType !== "customise") return;
    rows.forEach(async (row) => {
      if (row.productId && row.customizations === undefined) {
        setRows(rs => rs.map(r => r._id === row._id ? { ...r, customizations: [] } : r));
        try {
          const res = await customizationsApi.getByProduct(row.productId);
          if (res?.success) setRows(rs => rs.map(r => r._id === row._id ? { ...r, customizations: res.data || [] } : r));
        } catch { /* ignore */ }
      }
    });
  }, [rows, productType]);


  const handleReorder = () => {
    if (!lastOrder) return;
    const orderType = (lastOrder.order_type === "customized" || lastOrder.order_type === "customise")
      ? "printed"
      : lastOrder.product_category || "local_retail";
    setProductType(orderType);
    const reorderRows = (lastOrder.items || []).map(item => {
      const match = allProducts.find(p => p.id === item.product_id);
      const customizationValues = {};
      if (Array.isArray(item.customizations)) {
        item.customizations.forEach(c => {
          const key = c.customization_id || c.customizationId || c.customization?.id;
          if (key) customizationValues[key] = c.value || "";
        });
      }
      return {
        _id: crypto.randomUUID(),
        productId: item.product_id,
        name: item.product?.name || "",
        sku: item.product?.sku || "",
        qty: item.quantity,
        price: item.unit_price ? parseFloat(item.unit_price) : "",
        minPrice: match ? match.min_price : parseFloat(item.product?.min_price || 0),
        customizationValues: orderType === "printed" ? customizationValues : {},
      };
    });
    setRows(reorderRows);
    setStep(isOrgMode ? 3 : 2);
    toast.success("Loaded previous order! Please review and confirm.");
  };

  const validRows = rows.filter(r => r.productId && Number(r.qty) > 0);
  const grandTotal = validRows.reduce((s, r) => s + Number(r.qty) * (parseFloat(r.price) || 0), 0);

  const canNext = () => {
    if (step === 0) {
      const hasCust = isOrgMode ? true : !!customer;
      return !!(hasCust && clientType && productType && salesExec);
    }
    // step 1 = Products
    if (step === 1) {
      if (validRows.length === 0) return false;
      if (validRows.some(r => parseFloat(r.price || 0) < r.minPrice)) return false;
      if (productType === "customise") {
        for (const r of validRows) {
          if (r.customizations) {
            for (const a of r.customizations) {
              const cust = a.customization;
              if (cust?.is_required) {
                const val = r.customizationValues?.[cust.id];
                if (!val || (typeof val === "string" && !val.trim())) return false;
              }
            }
          }
        }
      }
      return true;
    }
    // Step 2: Location (non-org, mandatory) or Branch Qty (org)
    if (step === 2 && !isOrgMode) return !!selectedLocation;
    if (step === 2 && isOrgMode) {
      for (const r of validRows) {
        const allocated = branches.reduce((sum, b) => sum + (branchQty[b.id]?.[r.productId] ?? 0), 0);
        if (allocated > (r.fixedQty ?? r.qty)) return false;
      }
      return true;
    }
    return true;
  };

  const next = () => {
    if (!canNext()) return;
    if (step === 1 && isOrgMode) {
      setRows(rs => rs.map(r => ({ ...r, fixedQty: r.qty })));
    }
    setStep(s => s + 1);
  };

  const back = () => {
    if (step === 2 && isOrgMode) {
      setRows(rs => rs.map(r => ({ ...r, qty: r.fixedQty !== undefined ? r.fixedQty : r.qty })));
    }
    if (step > 0) { setStep(s => s - 1); return; }
    if (isEmbedded) onBack();
    else navigate(-1);
  };

  const handleSubmit = async () => {
    if (validRows.length === 0) { toast.error("Add at least one product"); return; }
    const invalidRows = validRows.filter(r => parseFloat(r.price || 0) < r.minPrice);
    if (invalidRows.length > 0) { toast.error(`Price for "${invalidRows[0].name}" is below minimum (₹${invalidRows[0].minPrice.toFixed(2)})`); return; }

    setPlacing(true);
    try {
      let res;

      if (isOrgMode) {
        const orgItems = [];
        branches.forEach(b => {
          validRows.forEach(r => {
            const qty = branchQty[b.id]?.[r.productId] ?? 0;
            if (qty > 0) orgItems.push({ lead_id: b.id, product_id: r.productId, quantity: qty, unit_price: parseFloat(r.price) });
          });
        });
        if (orgItems.length === 0) { toast.error("Please enter at least one non-zero quantity for a branch."); setPlacing(false); return; }
        res = await ordersApi.create({ status: "pending", organisation_id: parseInt(orgIdParam), items: orgItems, order_type: productType === "printed" ? "printed" : "plain", product_category: productType || "local_retail", client_type: clientType, sales_executive: salesExec || undefined, invoice_mode: invoiceMode, ...(deliveryDate ? { delivery_date: deliveryDate } : {}) });
      } else {
        const fileMap = {};
        const items = validRows.map((r, rowIdx) => {
          const item = { product_id: r.productId, quantity: Number(r.qty), ...(parseFloat(r.price) >= 0 ? { unit_price: parseFloat(r.price) } : {}) };
          if (productType === "customise" && r.customizations && r.customizationValues) {
            item.customizations = Object.entries(r.customizationValues).map(([custId, val]) => {
              if (val instanceof File) {
                const key = `file_${rowIdx}_${custId}`;
                fileMap[key] = val;
                return { customization_id: parseInt(custId, 10), file_key: key };
              }
              const s = String(val);
              return s ? { customization_id: parseInt(custId, 10), value: s } : null;
            }).filter(Boolean);
          }
          return item;
        });

        const jsonPayload = { lead_id: customer.id, status: "pending", items, order_type: productType === "printed" ? "printed" : "plain", product_category: productType || "local_retail", client_type: clientType, sales_executive: salesExec || undefined, delivery_details: selectedLocation ? formatAddress(selectedLocation) : undefined, ...(deliveryDate ? { delivery_date: deliveryDate } : {}) };

        if (Object.keys(fileMap).length > 0) {
          const formData = new FormData();
          formData.append("data", JSON.stringify(jsonPayload));
          Object.entries(fileMap).forEach(([key, file]) => formData.append(key, file));
          res = await ordersApi.createWithFiles(formData);
        } else {
          res = await ordersApi.create(jsonPayload);
        }
      }

      const groupRef = res.data?.data?.group_ref;
      const orderId = res.data?.data?.order_id || res.data?.data?.id || res.data?.id;
      toast.success("Order placed successfully!");

      if (isEmbedded && onOrderPlaced) {
        onOrderPlaced({ customer: isOrgMode ? orgNameParam : customer.name, items: validRows, total: grandTotal });
      } else if (isOrgMode && groupRef) {
        navigate(`/orders?group_ref=${groupRef}`);
      } else {
        navigate(orderId ? `/orders/${orderId}` : "/orders");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to place order.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="page-wrapper po-page">
      <div className="po-page-header">
        <button className="po-back-btn" onClick={back}>
          <ChevronLeft size={16} /> Back
        </button>
        <div>
          <h1 className="page-title">
            {isOrgMode ? `Place Org Order — ${orgNameParam}` : "Place New Order"}
          </h1>
          {!isOrgMode && customer && <p className="page-desc">For <strong>{customer.name}</strong></p>}
        </div>
      </div>

      <Stepper step={step} steps={activeSteps} />

      <div className="po-card">
        {step === 0 && (
          <StepCustomer
            selected={customer} onSelect={setCustomer}
            lastOrder={lastOrder} loadingLastOrder={loadingLastOrder} onReorder={handleReorder}
            orgMode={isOrgMode} orgName={orgNameParam}
            clientType={clientType} onChangeClientType={setClientType}
            productType={productType} onChangeProductType={setProductType}
            salesExec={salesExec} onChangeSalesExec={setSalesExec}
            allSalesStaff={allSalesStaff}
            onNext={next} canNext={canNext()}
          />
        )}
        {step === 1 && <StepProducts rows={rows} setRows={setRows} allProducts={allProducts} categories={categories} productType={productType} />}
        {step === 2 && !isOrgMode && (
          <StepLocation
            locations={locations} loading={locLoading}
            selected={selectedLocation} onSelect={setSelectedLocation}
            lastOrderedAddress={lastOrderedAddress}
            onRestoreLast={() => {
              if (lastOrderedAddress) {
                const matched = locations.find(l => formatAddress(l) === lastOrderedAddress.delivery_details);
                if (matched) setSelectedLocation(matched);
              }
            }}
            searchQuery={locSearch} setSearchQuery={setLocSearch}
            page={locPage} setPage={setLocPage}
            totalPages={locTotalPages}
            onAddLocation={handleAddLocation}
          />
        )}
        {step === 2 && isOrgMode && (
          <StepBranchQty
            branches={branches} setBranches={setBranches} loadingBranches={loadingBranches}
            rows={rows} setRows={setRows} branchQty={branchQty} setBranchQty={setBranchQty}
            orgName={orgNameParam}
          />
        )}
        {step === 3 && (
          <StepReview
            customer={isOrgMode ? { name: orgNameParam } : customer}
            rows={rows} selectedLocation={selectedLocation} productType={productType}
            isOrgMode={isOrgMode} branches={branches} branchQty={branchQty}
            invoiceMode={invoiceMode} setInvoiceMode={setInvoiceMode}
            deliveryDate={deliveryDate} setDeliveryDate={setDeliveryDate}
            onGoToStep={setStep} onSubmit={handleSubmit} placing={placing}
          />
        )}

        <div className="po-nav-footer">
          {step > 0
            ? <button className="btn-outline po-nav-btn" onClick={back} disabled={placing}><ChevronLeft size={16} /> Back</button>
            : <div />}

          {step < activeSteps.length - 1 && (
            <button className="btn-primary po-nav-btn" onClick={next} disabled={!canNext()}>
              {step === 1 && validRows.length === 0 ? "Add a product to continue" : `Next: ${activeSteps[step + 1].label}`}
              <ChevronRight size={16} />
            </button>
          )}
          {step === activeSteps.length - 1 && (
            <span style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>You can review and edit details before placing the order.</span>
          )}
        </div>
      </div>

    </div>
  );
}
